import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Alert,
  Chip,
  Collapse,
  Tooltip,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VerifiedIcon from '@mui/icons-material/Verified';
import SyncIcon from '@mui/icons-material/Sync';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import useAppStore from '../context/AppContext';
import { processFilesStreaming } from '../services/api';

// Processing stage definitions with professional messaging
const STAGE_CONFIG = {
  pending: {
    icon: HourglassEmptyIcon,
    color: 'disabled',
    label: 'Queued',
    description: 'Waiting to start',
  },
  extracting: {
    icon: SyncIcon,
    color: 'success',
    label: 'Pass 1',
    description: 'AI is analyzing the document',
    spinning: true,
  },
  ai_extraction: {
    icon: SyncIcon,
    color: 'success',
    label: 'Pass 1',
    description: 'Reading and extracting transactions',
    spinning: true,
  },
  validating: {
    icon: FactCheckIcon,
    color: 'info',
    label: 'Validating',
    description: 'Verifying data integrity',
    spinning: true,
  },
  validation: {
    icon: FactCheckIcon,
    color: 'info',
    label: 'Validating',
    description: 'Checking balance reconciliation',
    spinning: true,
  },
  rerunning: {
    icon: SyncIcon,
    color: 'warning',
    label: 'Pass 2',
    description: 'Requesting second opinion',
    spinning: true,
  },
  verification_rerun: {
    icon: SyncIcon,
    color: 'warning',
    label: 'Pass 2',
    description: 'Balance mismatch - verifying extraction',
    spinning: true,
  },
  complete: {
    icon: CheckCircleIcon,
    color: 'success',
    label: 'Complete',
    description: 'Successfully processed',
  },
  complete_with_warnings: {
    icon: WarningAmberIcon,
    color: 'warning',
    label: 'Review Needed',
    description: 'Completed with warnings',
  },
  error: {
    icon: ErrorIcon,
    color: 'error',
    label: 'Failed',
    description: 'Processing failed',
  },
};

function Processing() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [processingComplete, setProcessingComplete] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [expandedFile, setExpandedFile] = useState(null);

  // Use ref to prevent double execution in React 18 Strict Mode
  const hasStartedProcessing = useRef(false);
  const abortStreamRef = useRef(null);
  const isMountedRef = useRef(true);

  const {
    uploadedFiles,
    isProcessing,
    setProcessing,
    processingStatus,
    processingConfig,
    setProcessingStatus,
    setProcessingConfig,
    extractedData,
    setExtractedData,
    clearProcessingState,
    getSelectedModel,
  } = useAppStore();

  useEffect(() => {
    // Prevent double execution in React 18 Strict Mode
    let isActive = true;

    console.log('[Processing] useEffect triggered', {
      uploadedFilesCount: uploadedFiles.length,
      hasStarted: hasStartedProcessing.current,
      isProcessing,
      extractedDataCount: extractedData.length,
    });

    if (uploadedFiles.length === 0) {
      console.log('[Processing] No files, redirecting to home');
      navigate('/');
      return;
    }

    // Don't run if already processing (check both ref and state)
    if (hasStartedProcessing.current || isProcessing) {
      console.log('[Processing] Already processing, skipping');
      return;
    }

    // Don't run if data was already extracted (navigating back from preview)
    if (extractedData.length > 0) {
      console.log('[Processing] Data already extracted, showing complete');
      setProcessingComplete(true);
      return;
    }

    // Mark as started immediately (synchronous, prevents race condition)
    hasStartedProcessing.current = true;
    console.log('[Processing] Starting processing...');

    const runProcessing = () => {
      if (!isActive) return;
      setProcessing(true);

      // Initialize status for all files as pending
      const initialStatus = {};
      uploadedFiles.forEach((file) => {
        initialStatus[file.serverFilename] = {
          status: 'pending',
          stage: 'pending',
          message: 'Waiting in queue...',
          progress: 0,
        };
      });
      uploadedFiles.forEach((file) => {
        setProcessingStatus(file.serverFilename, initialStatus[file.serverFilename]);
      });

      const filenames = uploadedFiles.map((f) => f.serverFilename);
      const selectedModel = getSelectedModel();
      const selectedVerificationModel = useAppStore.getState().getSelectedVerificationModel();

      // Start streaming processing
      console.log('[Processing] Starting streaming with files:', filenames);
      console.log('[Processing] Models - Primary:', selectedModel, 'Verification:', selectedVerificationModel);
      abortStreamRef.current = processFilesStreaming(
        filenames,
        { model: selectedModel, verificationModel: selectedVerificationModel, autoRerun: true },
        {
          onStart: (data) => {
            console.log('[Processing] onStart:', data);
            setProcessingConfig({
              model: data.model,
              verificationModel: data.verificationModel,
              autoRerun: data.autoRerun,
              totalFiles: data.totalFiles,
              jobId: data.jobId,
            });
          },
          onStatus: (filename, statusData) => {
            console.log('[Processing] onStatus:', filename, statusData.status);
            setProcessingStatus(filename, statusData);
          },
          onComplete: (data) => {
            console.log('[Processing] onComplete called, isActive:', isActive, 'isMounted:', isMountedRef.current);

            // Use isMountedRef instead of isActive for StrictMode compatibility
            if (!isMountedRef.current) {
              console.log('[Processing] Component unmounted, ignoring complete');
              return;
            }

            const extractedResults = [];
            let errorCount = 0;

            data.results.forEach((r) => {
              if (r.success) {
                extractedResults.push({
                  filename: r.filename,
                  metadata: r.data.statement_metadata || {},
                  transactions: r.data.transactions || [],
                  processingMetadata: r.metadata,
                });
              } else {
                errorCount++;
              }
            });

            console.log('[Processing] Extracted results:', extractedResults.length, 'errors:', errorCount);

            setExtractedData(extractedResults);
            setHasErrors(errorCount > 0);
            setProcessing(false);
            setProcessingComplete(true);

            if (errorCount === 0) {
              const rerunsUsed = data.summary.rerunsUsed || 0;
              if (rerunsUsed > 0) {
                enqueueSnackbar(
                  `All files processed! ${rerunsUsed} required verification rerun.`,
                  { variant: 'success' }
                );
              } else {
                enqueueSnackbar('All files processed successfully!', { variant: 'success' });
              }
              // Auto-navigate to preview after successful processing
              console.log('[Processing] Auto-navigating to preview');
              navigate('/preview');
            } else {
              enqueueSnackbar(`${errorCount} file(s) failed to process`, { variant: 'warning' });
            }
          },
          onError: (error) => {
            if (!isMountedRef.current) return;
            console.error('Processing stream error:', error);
            enqueueSnackbar(error.message || 'Processing failed', { variant: 'error' });

            // Mark all as error
            uploadedFiles.forEach((file) => {
              setProcessingStatus(file.serverFilename, {
                status: 'error',
                stage: 'error',
                message: error.message || 'Processing failed',
                progress: 100,
              });
            });
            setHasErrors(true);
            setProcessing(false);
            setProcessingComplete(true);
          },
        }
      );
    };

    runProcessing();

    return () => {
      isActive = false;
      // Don't abort on StrictMode remount - only abort when truly leaving
      // We use a small delay to distinguish StrictMode remount from real navigation
      setTimeout(() => {
        if (!isMountedRef.current && abortStreamRef.current) {
          console.log('[Processing] Aborting stream on true unmount');
          abortStreamRef.current();
        }
      }, 100);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track true unmount (not StrictMode remount) and reset refs
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Reset the processing flag on true unmount so it can restart if user navigates back
      setTimeout(() => {
        if (!isMountedRef.current) {
          hasStartedProcessing.current = false;
        }
      }, 150);
    };
  }, []);

  // Retry failed files
  const handleRetryFailed = async () => {
    const failedFiles = uploadedFiles.filter((file) => {
      const status = processingStatus[file.serverFilename];
      return status?.status === 'error';
    });

    if (failedFiles.length === 0) {
      enqueueSnackbar('No failed files to retry', { variant: 'info' });
      return;
    }

    setRetrying(true);
    setHasErrors(false);

    // Mark failed files as pending again
    failedFiles.forEach((file) => {
      setProcessingStatus(file.serverFilename, {
        status: 'pending',
        stage: 'pending',
        message: 'Retrying...',
        progress: 0,
      });
    });

    const filenames = failedFiles.map((f) => f.serverFilename);
    const selectedModel = getSelectedModel();
    const selectedVerificationModel = useAppStore.getState().getSelectedVerificationModel();

    // Start streaming retry
    abortStreamRef.current = processFilesStreaming(
      filenames,
      { model: selectedModel, verificationModel: selectedVerificationModel, autoRerun: true },
      {
        onStatus: (filename, statusData) => {
          setProcessingStatus(filename, statusData);
        },
        onComplete: (data) => {
          let newErrorCount = 0;
          const newExtractedResults = [...extractedData];

          data.results.forEach((r) => {
            if (r.success) {
              // Add to extracted data (or update if already exists)
              const existingIndex = newExtractedResults.findIndex(
                (d) => d.filename === r.filename
              );
              const newData = {
                filename: r.filename,
                metadata: r.data.statement_metadata || {},
                transactions: r.data.transactions || [],
                processingMetadata: r.metadata,
              };
              if (existingIndex >= 0) {
                newExtractedResults[existingIndex] = newData;
              } else {
                newExtractedResults.push(newData);
              }
            } else {
              newErrorCount++;
            }
          });

          setExtractedData(newExtractedResults);

          if (newErrorCount === 0) {
            enqueueSnackbar('All retry files processed successfully!', { variant: 'success' });
          } else {
            setHasErrors(true);
            enqueueSnackbar(`${newErrorCount} file(s) still failed`, { variant: 'warning' });
          }
          setRetrying(false);
        },
        onError: (error) => {
          console.error('Retry stream error:', error);
          enqueueSnackbar(error.message || 'Retry failed', { variant: 'error' });
          failedFiles.forEach((file) => {
            setProcessingStatus(file.serverFilename, {
              status: 'error',
              stage: 'error',
              message: 'Retry failed',
              progress: 100,
            });
          });
          setHasErrors(true);
          setRetrying(false);
        },
      }
    );
  };

  const getStatusConfig = (fileStatus) => {
    if (!fileStatus) {
      return STAGE_CONFIG.pending;
    }
    const status = fileStatus.status || fileStatus.stage || 'pending';
    return STAGE_CONFIG[status] || STAGE_CONFIG.pending;
  };

  const renderStatusIcon = (fileStatus) => {
    const config = getStatusConfig(fileStatus);
    const IconComponent = config.icon;
    const isSpinning = config.spinning && !processingComplete;

    return (
      <IconComponent
        color={config.color}
        sx={isSpinning ? { animation: 'spin 1.5s linear infinite' } : {}}
      />
    );
  };

  const renderStatusChip = (fileStatus) => {
    const config = getStatusConfig(fileStatus);
    // Show model name in chip when actively processing
    let label = config.label;
    if (fileStatus?.model && (config.spinning || fileStatus?.status === 'extracting' || fileStatus?.status === 'rerunning')) {
      const modelShort = fileStatus.model.split('/').pop();
      label = `${config.label}: ${modelShort}`;
    }
    return (
      <Chip
        label={label}
        color={config.color === 'disabled' ? 'default' : config.color}
        size="small"
        variant="outlined"
        sx={{ minWidth: 120 }}
      />
    );
  };

  const renderFileDetails = (file) => {
    const fileStatus = processingStatus[file.serverFilename];
    if (!fileStatus) return null;

    const isProcessing = fileStatus.status !== 'complete' &&
                         fileStatus.status !== 'complete_with_warnings' &&
                         fileStatus.status !== 'error' &&
                         fileStatus.status !== 'pending';

    return (
      <Collapse in={expandedFile === file.id} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 7, pr: 2, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {fileStatus.message}
          </Typography>
          {isProcessing && fileStatus.model && (
            <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1 }}>
              Using: {fileStatus.model}
            </Typography>
          )}
          {fileStatus.progress > 0 && fileStatus.progress < 100 && (
            <LinearProgress
              variant="determinate"
              value={fileStatus.progress}
              sx={{ height: 4, borderRadius: 2, mt: 1 }}
            />
          )}
          {fileStatus.rerunUsed && !fileStatus.rerunAlsoFailed && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <VerifiedIcon color="success" fontSize="small" />
              <Typography variant="caption" color="success.main">
                Pass 2 successful - verified by {fileStatus.rerunModel?.split('/').pop() || 'secondary model'}
              </Typography>
            </Box>
          )}
          {fileStatus.rerunAlsoFailed && (
            <Alert severity="warning" sx={{ mt: 1 }} variant="outlined">
              Both Pass 1 and Pass 2 produced unbalanced results. Manual review recommended.
            </Alert>
          )}
          {fileStatus.transactionCount > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {fileStatus.transactionCount} transactions extracted
            </Typography>
          )}
        </Box>
      </Collapse>
    );
  };

  // Calculate progress
  const completedCount = Object.values(processingStatus).filter((s) => {
    const status = s?.status || s;
    return status === 'complete' || status === 'complete_with_warnings';
  }).length;
  const errorCount = Object.values(processingStatus).filter((s) => {
    const status = s?.status || s;
    return status === 'error';
  }).length;
  const totalCount = uploadedFiles.length;
  const progress = totalCount > 0 ? ((completedCount + errorCount) / totalCount) * 100 : 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Processing Statements
      </Typography>

      {!processingComplete && (
        <>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            AI is analyzing your bank statements and extracting transactions.
          </Typography>

          {processingConfig && (
            <Alert severity="info" sx={{ mb: 3 }} icon={<SyncIcon />}>
              <Typography variant="body2">
                <strong>Primary Model:</strong> {processingConfig.model?.split('/').pop()}
                {processingConfig.autoRerun && (
                  <>
                    {' | '}
                    <strong>Verification Model:</strong> {processingConfig.verificationModel?.split('/').pop()}
                  </>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Files with balance discrepancies will automatically be re-analyzed for accuracy.
              </Typography>
            </Alert>
          )}

          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1" sx={{ flex: 1 }}>
                Progress: {completedCount + errorCount} of {totalCount} files
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Paper>
        </>
      )}

      {processingComplete && (
        <Alert
          severity={hasErrors ? 'warning' : 'success'}
          sx={{ mb: 3 }}
          icon={hasErrors ? <WarningAmberIcon /> : <CheckCircleIcon />}
        >
          {hasErrors
            ? 'Processing complete with some errors. You can still proceed with successfully processed files.'
            : 'All files processed successfully!'}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <List disablePadding>
          {uploadedFiles.map((file, index) => {
            const fileStatus = processingStatus[file.serverFilename];
            const statusConfig = getStatusConfig(fileStatus);

            return (
              <Box key={file.id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() =>
                    setExpandedFile(expandedFile === file.id ? null : file.id)
                  }
                >
                  <ListItemIcon>{renderStatusIcon(fileStatus)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">
                          {file.originalName || file.name}
                        </Typography>
                        {fileStatus?.rerunUsed && !fileStatus?.rerunAlsoFailed && (
                          <Tooltip title="Verified by secondary model">
                            <VerifiedIcon color="success" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {fileStatus?.message || statusConfig.description}
                      </Typography>
                    }
                  />
                  <Box sx={{ ml: 2 }}>{renderStatusChip(fileStatus)}</Box>
                </ListItem>
                {renderFileDetails(file)}
              </Box>
            );
          })}
        </List>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            // Abort any ongoing stream
            if (abortStreamRef.current) {
              abortStreamRef.current();
            }
            // Clear processing state so user can modify files and reprocess
            clearProcessingState();
            navigate('/');
          }}
          disabled={isProcessing || retrying}
        >
          Back to Upload
        </Button>

        {processingComplete && hasErrors && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={
              retrying ? (
                <AutorenewIcon sx={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <RefreshIcon />
              )
            }
            onClick={handleRetryFailed}
            disabled={retrying}
          >
            {retrying ? 'Retrying...' : 'Retry Failed Files'}
          </Button>
        )}

        {processingComplete && completedCount > 0 && (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/preview')}
            disabled={retrying}
          >
            Review Transactions
          </Button>
        )}
      </Box>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
}

export default Processing;
